"use client";

import Link from "next/link";
import { ChevronRight } from "lucide-react";

/* ─── Types ────────────────────────────────────────────────────────────────── */

interface SitemapLink {
  label: string;
  href: string;
}

interface SitemapSection {
  title: string;
  links: SitemapLink[];
}

/* ─── Data ─────────────────────────────────────────────────────────────────── */

const SITEMAP_SECTIONS: SitemapSection[] = [
  {
    title: "Main Pages",
    links: [
      { label: "Home", href: "/" },
      { label: "About Us", href: "/about" },
      { label: "Contact Us", href: "/contact" },
      { label: "Blog", href: "/blog" },
      { label: "Careers", href: "/careers" },
      { label: "360° Business Support", href: "/360-support" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "All Services", href: "/services" },
      { label: "Business Registration", href: "/services" },
      { label: "Startup Funding", href: "/services/funding" },
      { label: "Loans & NBFC Assistance", href: "/services/funding/loans" },
      { label: "NBFC Coordination", href: "/services/funding/nbfc" },
      { label: "Government Grants & Subsidies", href: "/services/funding/grants" },
      { label: "Legal & Compliance", href: "/services/legal" },
      { label: "Certification & Registrations", href: "/services/certification" },
      { label: "Digital Marketing", href: "/services/marketing" },
      { label: "Growth Branding", href: "/services/marketing/growthbranding" },
      { label: "Performance Marketing", href: "/services/marketing/performancemarketing" },
    ],
  },
  {
    title: "IT & Technology Solutions",
    links: [
      { label: "IT Solutions Overview", href: "/services/it" },
      { label: "Web Development", href: "/services/it/webdev" },
      { label: "App Development", href: "/services/it/appdev" },
      { label: "Software Development", href: "/services/it/software" },
      { label: "Cloud Services", href: "/services/it/cloud" },
      { label: "CRM Solutions", href: "/services/it/crm" },
      { label: "ERP Solutions", href: "/services/it/erp" },
      { label: "AI & Machine Learning", href: "/services/it/aiml" },
      { label: "Cybersecurity", href: "/services/it/cybersecurity" },
      { label: "DevOps & CI/CD", href: "/services/it/devops" },
      { label: "Hosting & Infrastructure", href: "/services/it/hosting" },
    ],
  },
  {
    title: "Government Schemes & Registrations",
    links: [
      { label: "Government Schemes Overview", href: "/services/GovernmentSchemes" },
      { label: "DPIIT & Startup India Registration", href: "/services/certification" },
      { label: "MSME & Udyam Registration", href: "/services/certification" },
      { label: "NIDHI Programme (DST)", href: "/services/GovernmentSchemes" },
      { label: "Seed Fund Assistance", href: "/services/funding/grants" },
      { label: "GeM Registration & Selling", href: "/services/GovernmentSchemes" },
      { label: "NSIC Registration", href: "/services/GovernmentSchemes" },
      { label: "ZED Certification", href: "/services/GovernmentSchemes" },
      { label: "ISO Certification", href: "/services/certification" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog & Insights", href: "/blog" },
      { label: "Guides & Articles", href: "/blog" },
      { label: "Case Studies", href: "/blog" },
      { label: "FAQs", href: "/contact" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "Refund & Cancellation Policy", href: "/refund" },
      { label: "Sitemap", href: "/sitemap" },
    ],
  },
];

/* ─── Page ─────────────────────────────────────────────────────────────────── */

export default function SitemapPage() {
  return (
    <main className="bg-white font-['DM_Sans',sans-serif] text-[#333]">
      {/* ── Hero ── */}
      <section className="bg-[#0D1B3E] py-16 text-center text-white">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-[0.6rem] font-bold uppercase tracking-[0.25em] text-[#C95A0E]">
            Navigation
          </p>
          <h1 className="mt-2 font-['Cormorant_Garamond',Georgia,serif] text-[1.75rem] font-semibold leading-tight sm:text-[2.25rem]">
            Sitemap
          </h1>
          <p className="mt-2 text-[0.75rem] text-white/60 max-w-lg mx-auto">
            A clear overview of all sections and pages on our platform — helping you and search engines navigate easily.
          </p>
        </div>
      </section>

      {/* ── Sitemap Grid ── */}
      <section className="mx-auto max-w-5xl px-6 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {SITEMAP_SECTIONS.map((section) => (
            <div key={section.title} className="group">
              {/* Section title */}
              <div className="flex items-center gap-2 mb-4 pb-2 border-b-2 border-[#0D1B3E]">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C95A0E] flex-shrink-0" />
                <h2 className="text-[0.85rem] font-bold text-[#0D1B3E] uppercase tracking-wide">
                  {section.title}
                </h2>
              </div>

              {/* Links */}
              <ul className="space-y-1.5">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="group/link flex items-center gap-1.5 text-[0.78rem] text-[#555] hover:text-[#1565C0] transition-colors duration-200"
                    >
                      <ChevronRight className="w-3 h-3 text-[#C95A0E] opacity-50 group-hover/link:opacity-100 transition-opacity flex-shrink-0" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* ── Need Help ── */}
        <div className="mt-14 rounded-lg border border-[#C95A0E]/20 bg-[#FDF8F4] px-6 py-6 text-center">
          <p className="text-[0.85rem] font-semibold text-[#0D1B3E]">
            Need Help Finding Something?
          </p>
          <p className="mt-2 text-[0.75rem] text-[#5A3E2B]">
            If you are unable to find a specific page or service, please visit our{" "}
            <Link href="/contact" className="text-[#1565C0] underline font-medium">
              Contact page
            </Link>
            . Our team will be happy to assist you.
          </p>
        </div>

        <p className="mt-8 text-center text-[0.65rem] text-gray-400">
          This sitemap is regularly updated to reflect the latest structure of our website.
        </p>
      </section>
    </main>
  );
}
